import { Eye } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { FeeTemplate, GenerateFormData } from './types';
import { formatCurrency } from './utils';

interface GenerateInvoicesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: GenerateFormData;
  onFormChange: (form: GenerateFormData) => void;
  onPreview: () => void;
  feeTemplates: FeeTemplate[];
  apartmentCount: number;
}

export function GenerateInvoicesDialog({
  open,
  onOpenChange,
  form,
  onFormChange,
  onPreview,
  feeTemplates,
  apartmentCount
}: GenerateInvoicesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generera fakturor</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Period</Label>
            <Input
              type="month"
              value={form.period}
              onChange={(e) => onFormChange({ ...form, period: e.target.value })}
              data-testid="generate-period-input"
            />
          </div>
          <div>
            <Label>Avgiftsmall *</Label>
            <Select
              value={form.fee_template_id}
              onValueChange={(value) => onFormChange({ ...form, fee_template_id: value })}
            >
              <SelectTrigger data-testid="generate-fee-template-select">
                <SelectValue placeholder="Välj mall" />
              </SelectTrigger>
              <SelectContent>
                {feeTemplates.filter(f => f.is_active).map(fee => (
                  <SelectItem key={fee.id} value={fee.id}>
                    {fee.name} ({formatCurrency(fee.base_amount)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Förfallodagar</Label>
            <Input
              type="number"
              value={form.due_days}
              onChange={(e) => onFormChange({ ...form, due_days: e.target.value })}
              data-testid="generate-due-days-input"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="include-adj"
                checked={form.include_adjustments}
                onChange={(e) => onFormChange({ ...form, include_adjustments: e.target.checked })}
                data-testid="generate-include-adjustments-checkbox"
              />
              <label htmlFor="include-adj" className="text-sm">Inkludera rabatter/pålägg</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="include-shared"
                checked={form.include_shared_costs}
                onChange={(e) => onFormChange({ ...form, include_shared_costs: e.target.checked })}
                data-testid="generate-include-shared-checkbox"
              />
              <label htmlFor="include-shared" className="text-sm">Inkludera delade kostnader</label>
            </div>
          </div>
          <p className="text-sm text-slate-500">
            Fakturor genereras för {apartmentCount} lägenheter
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={onPreview} data-testid="generate-preview-btn">
            <Eye size={16} className="mr-2" />
            Förhandsgranska
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
